import { useState, useEffect, useCallback } from 'react';
import { getTripBySlug, GenerateTripResponse } from '../services/api';
import { logger } from '../utils/logger';

/**
 * Hook to manage trip data fetching and updates.
 * 
 * @param slug - Trip slug
 * @param editToken - Optional editToken for shared link access (view-only, sent in X-Edit-Token header)
 */
export function useTrip(slug: string | undefined, editToken?: string) {
    const [tripData, setTripData] = useState<GenerateTripResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchTrip = useCallback(async () => {
        if (!slug) return;
        setLoading(true);
        try {
            const data = await getTripBySlug(slug, editToken);
            if (!data) {
                setError('Trip not found');
            } else {
                setTripData(data);
                setError(null);
            }
        } catch (err) {
            logger.error('Failed to fetch trip:', err);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [slug, editToken]);

    useEffect(() => {
        fetchTrip();
    }, [fetchTrip]);

    const updateTripData = (data: Partial<GenerateTripResponse>) => {
        setTripData(prev => prev ? { ...prev, ...data } : null);
    };

    return { tripData, loading, error, fetchTrip, updateTripData };
}
